/** @jsx jsx */

import React, { useState } from "react";

import { jsx } from "@emotion/core";
import { Field, FieldProps, ErrorMessage } from "formik";
import {
  Input,
  Tag,
  Icon,
  Modal,
  Typography,
  Tooltip,
  Col,
  Row,
  Button
} from "antd";

import { formHelperStyles, ModalInputTagsStyles } from "./FormHelperStyles";
import infoIcon from "../../assets/info-tooltip.svg";
import {
  GstinStatusField,
  formHelperLabels as LABELS
} from "./FormHelperLabels";

const { Text } = Typography;

export interface MultiTags {
  id: number;
  loading?: boolean;
  value: string;
  /**
   * indicates if user has closed the TAG
   */
  removed: boolean;
  isActive?: boolean;
  error?: string;
}

interface Props {
  name: string;

  className?: string;
  placeholder?: string;
  /**
   * A key in formik, which stores an array of all the added Tags.
   * NOTE: it should be of type, MultiTags
   */
  listName: string;

  mandatory?: boolean;

  listItems: MultiTags[];

  label?: string;

  loading?: boolean;

  disabled?: boolean;

  onAdd: (
    data: string
  ) => Promise<Partial<object | void>> | Partial<object | void>;
  onClose?: (data: string) => Promise<void> | void;

  tooltipText?: string;
}

/**
 *
 * A reusable component, which can accept input with a TextBox,
 * Call "onAdd" function, maintain loading,error states for each and individual TAG.
 * calls, "onClose" to close the TAG.
 * Updates FORMIK state, so there is no duplicate state just for this.
 *
 * NOTE: this component is to be used inside of the context of "formik"
 */
const FormMultiInputTags: React.FC<Props> = (props: Props) => {
  const {
    name,
    placeholder,
    className,
    listName,
    listItems,
    label,
    onAdd,
    disabled,
    onClose,
    loading,
    tooltipText,
    mandatory
  } = props;
  const [open, setOpen] = useState(false);
  const handleCancel = () => setOpen(false);
  const showMore = () => {
    setOpen(true);
  };
  /**
   * to get the "key" which is to be updated by formik
   */
  const getPath = (index: number, key?: string) =>
    `${listName}[${index}]${key ? `.${key}` : ""}`;

  return (
    <section css={[formHelperStyles]} className={className}>
      <label className="d-flex align-items-center">
        <span className="pr-1">
          {label}
          <Text className="mandatory">{mandatory && LABELS.mandatory}</Text>
        </span>
        {tooltipText && (
          <Tooltip title={tooltipText}>
            <Icon component={() => <img src={infoIcon} alt="info" />} />
          </Tooltip>
        )}
      </label>

      <Field name={name}>
        {({
          field,
          form: { setFieldValue, setFieldTouched },
          meta: { error }
        }: FieldProps) => {
          const handleAdd = async (
            e: React.KeyboardEvent | React.MouseEvent
          ) => {
            e.preventDefault();
            if (error || disabled || !field.value) {
              //to show error message
              setFieldTouched(name);
            } else {
              const currentIndex = listItems.length;

              try {
                const newItem = {
                  id: currentIndex,
                  value: field.value,
                  loading: true,
                  removed: false
                };
                setFieldValue(listName, [...listItems, newItem]);

                //clear value of input
                setFieldTouched(name, false);
                setFieldValue(name, "", false);

                setFieldTouched(getPath(currentIndex, "value"));

                //call handler
                const response = await onAdd(field.value);
                setFieldValue(
                  getPath(currentIndex, GstinStatusField.isActive),
                  response[GstinStatusField.isActive]
                );

                setFieldValue(getPath(currentIndex, "error"), undefined);
              } catch (error) {
                setFieldValue(
                  getPath(currentIndex, "error"),
                  error.message || error
                );
              } finally {
                setFieldValue(getPath(currentIndex, "loading"), false);
              }
            }
          };

          const handleClose = (item: MultiTags, itemId: number) => async () => {
            if (!item.loading) {
              try {
                setFieldValue(getPath(itemId, "loading"), true);

                if (onClose) {
                  await onClose(item.value);
                }
                setFieldValue(getPath(itemId, "removed"), true);
              } catch (error) {
                setFieldValue(getPath(itemId, "error"), error.message || error);
              } finally {
                setFieldValue(getPath(itemId, "loading"), false);
              }
            }
          };

          const inputTags = (list: MultiTags[], isModal: boolean) => {
            const renderInactiveMessage = (
              <span className={`in-active ${isModal ? "pl-3" : "pl-1"}`}>
                {LABELS.gstinWarning}
              </span>
            );
            return list
              .filter(item => !item.removed)
              .map((item: MultiTags) => (
                <Col key={item.id} span={isModal ? 8 : 10}>
                  <Tag
                    visible={!item.removed}
                    closable={!item.loading && !disabled}
                    onClose={handleClose(item, item.id)}
                    className={`px-2 mr-1 mb-1 d-flex align-items-center ant-tag-ellipse ${
                      item.error
                        ? "ant-tag-red"
                        : item.loading
                        ? ""
                        : "ant-tag-green"
                    }`}
                  >
                    <Text className="ant-tag-name-ellipsis">
                      <div>{item.value}</div>
                    </Text>
                    {item.loading && <Icon type="loading" />}
                  </Tag>
                  {!item.removed &&
                    !item.loading &&
                    (item.error ? (
                      <span className={`error ${isModal ? "pl-3" : "pl-1"}`}>
                        {item.error}
                      </span>
                    ) : (
                      // dont show the inactive message if isActive is not defined.
                      item?.isActive === false && renderInactiveMessage
                    ))}
                </Col>
              ));
          };
          const renderModal = (
            <Modal
              visible={open}
              title={FormMultiInputTagsLabels.modal.title}
              footer={null}
              onCancel={handleCancel}
              centered
              css={[ModalInputTagsStyles, formHelperStyles]}
            >
              <Row gutter={8} type="flex" className="flex-grow-1">
                {inputTags(listItems, true)}
              </Row>
            </Modal>
          );
          return (
            <React.Fragment>
              <Input
                {...field}
                disabled={disabled}
                placeholder={placeholder || ""}
                onPressEnter={handleAdd}
                suffix={
                  <Button type="link" onClick={handleAdd}>
                    <Icon type="plus-circle" />
                    {FormMultiInputTagsLabels.buttons.add}
                  </Button>
                }
                prefix={loading && <Icon type="loading" />}
              />
              <ErrorMessage name={name}>
                {msg => <Typography className="error">{msg}</Typography>}
              </ErrorMessage>
              <Row gutter={8} className="my-3">
                {inputTags(
                  listItems
                    .filter(item => !item.removed)
                    .slice(-2)
                    .reverse(),
                  false
                )}
                {listItems?.filter(item => !item.removed).length > 2 && (
                  <Col span={4}>
                    <Tag
                      className="text-label-more mr-1 mt-1"
                      onClick={showMore}
                    >
                      +{listItems.filter(item => !item.removed).length - 2} more
                    </Tag>
                  </Col>
                )}
                {renderModal}
              </Row>
            </React.Fragment>
          );
        }}
      </Field>
    </section>
  );
};

export default FormMultiInputTags;

const FormMultiInputTagsLabels = {
  modal: {
    title: "GSTID"
  },
  buttons: {
    add: "Add"
  }
};
